'use client';

import { IconChevronDownFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-down-filled';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { expandIconRecipe } from '../../styles/expand-icon.styles';
import { Icon } from '../icon';
import { AccordionItemContext } from './context/accordion-item.context';
import { accordionHeaderContentStyles } from './styles/accordion-header-content.styles';
import type { AccordionHeaderContentPropsInterface } from './types/accordion-header-content.types';

/**
 * Visual content for `Accordion.Header`.
 */
export const AccordionHeaderContent = forwardRef<
  HTMLDivElement,
  AccordionHeaderContentPropsInterface
>((props: AccordionHeaderContentPropsInterface, forwardedRef) => {
  const { children, className, css: cssProp, ...rest } = props;

  const { expanded, value } = useContext(AccordionItemContext);

  const layoutStyles = css(accordionHeaderContentStyles);
  const contentStyles = css(accordionHeaderContentStyles, cssProp);
  const iconStyles = css(expandIconRecipe.raw({ open: !!expanded }));

  const headerContentId = `${value}-header`;

  return (
    <div
      ref={forwardedRef}
      data-c11n-component="Accordion.HeaderContent"
      data-c11n-version={__C11N_VERSION__}
      className={cx('c11n', 'c11n_accordion__header-content-layout', layoutStyles)}
      {...rest}
      id={headerContentId}
    >
      <span className={cx('c11n', iconStyles)} aria-hidden="true">
        <Icon render={<IconChevronDownFilled />} />
      </span>
      <div className={cx('c11n', 'c11n_accordion__header-content', contentStyles, className)}>
        {children}
      </div>
    </div>
  );
});

AccordionHeaderContent.displayName = 'Accordion.HeaderContent';
